import type { LoginKind } from "@/features/auth/api/login";
import type { LoginStep } from "@/features/auth/hooks/useOtpLogin";
import { Eye, EyeOff, KeyRound, LockKeyhole, LogIn, Mail } from "lucide-react";
import { FormEvent } from "react";
import { signInFormText } from "./signInText";

type LoginFormProps = {
  step: LoginStep;
  kind: LoginKind;
  email: string;
  password: string;
  otp: string;
  showPassword: boolean;
  isSendingOtp: boolean;
  isVerifyingOtp: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onOtpChange: (value: string) => void;
  onTogglePassword: () => void;
  onCredentialsSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onOtpSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onBack: () => void;
};

export default function LoginForm({
  step,
  kind,
  email,
  password,
  otp,
  showPassword,
  isSendingOtp,
  isVerifyingOtp,
  onEmailChange,
  onPasswordChange,
  onOtpChange,
  onTogglePassword,
  onCredentialsSubmit,
  onOtpSubmit,
  onBack,
}: LoginFormProps) {
  const formText = signInFormText[kind];

  const displayValue = email.startsWith("+91 ") ? email.replace(/^\+91\s*/, "") : email;

  const handleIdentifierChange = (val: string) => {
    let cleanVal = val.trim();
    // Handle pasting numbers with +91 or 91 country codes
    if (cleanVal.startsWith("+91")) {
      cleanVal = cleanVal.slice(3).trim();
    } else if (cleanVal.startsWith("91") && cleanVal.replace(/\D/g, "").length >= 12) {
      cleanVal = cleanVal.slice(2).trim();
    }

    if (email.startsWith("+91 ")) {
      const digits = cleanVal.replace(/\D/g, "").slice(0, 10);
      if (digits.length > 0) {
        onEmailChange(`+91 ${digits}`);
      } else {
        onEmailChange("");
      }
    } else {
      if (/^\d/.test(cleanVal)) {
        const digits = cleanVal.replace(/\D/g, "").slice(0, 10);
        onEmailChange(`+91 ${digits}`);
      } else {
        onEmailChange(cleanVal);
      }
    }
  };

  if (step === "otp") {
    return (
      <form onSubmit={onOtpSubmit} className="space-y-4">
        <div className="rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface-muted)] px-3 py-2 text-sm text-[var(--text-muted)]">
          Code sent to <span className="font-medium text-[var(--text)]">{email}</span>
        </div>

        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-[var(--text)]">Verification code</span>
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]" />
            <input
              value={otp}
              onChange={(event) => onOtpChange(event.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              required
              pattern="\d{4,6}"
              maxLength={6}
              disabled={isVerifyingOtp}
              className="ui-focus h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] pl-10 pr-3 text-sm tracking-[0.3em] text-[var(--text)] placeholder:tracking-normal placeholder:text-[var(--text-subtle)] disabled:opacity-70"
              placeholder="123456"
            />
          </div>
        </label>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isVerifyingOtp}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-4 text-sm font-semibold text-[var(--text)] transition hover:bg-[var(--surface-muted)] disabled:opacity-70"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isVerifyingOtp}
            className="inline-flex h-10 flex-[1.4] items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[var(--accent)] px-4 text-sm font-semibold text-[var(--text-inverse)] transition hover:bg-[var(--accent-hover)] disabled:opacity-70"
          >
            <LogIn className="h-4 w-4" />
            {isVerifyingOtp ? "Signing in..." : formText.submitOtp}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onCredentialsSubmit} className="space-y-4">
      <label className="block">
        <span className="mb-2 block text-sm font-semibold text-[var(--text)]">Email or Phone</span>
        <div className="relative flex items-center h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] transition-colors focus-within:border-[var(--accent)] overflow-hidden">
          {email.startsWith("+91 ") ? (
            <div className="flex h-full items-center border-r border-[var(--border)] bg-[var(--surface-muted)] px-3 text-sm font-medium text-[var(--text-muted)] shrink-0 select-none">
              +91
            </div>
          ) : (
            <div className="pl-3 pr-2 flex items-center justify-center shrink-0">
              <Mail className="h-4 w-4 text-[var(--text-subtle)]" />
            </div>
          )}
          <input
            value={displayValue}
            onChange={(event) => handleIdentifierChange(event.target.value)}
            autoComplete="username"
            type={email.startsWith("+91 ") ? "tel" : "text"}
            required
            disabled={isSendingOtp}
            className="h-full w-full bg-transparent px-2 text-sm text-[var(--text)] placeholder:text-[var(--text-subtle)] outline-none disabled:opacity-70"
            placeholder={email.startsWith("+91 ") ? "9876543210" : "you@company.com or 9876543210"}
          />
        </div>
      </label>

      <div className="block">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-semibold text-[var(--text)]">Password</span>
          {kind === "user" && (
            <a
              href="/forgot-password"
              className="text-xs font-semibold text-[var(--accent)] hover:underline"
            >
              Forgot password?
            </a>
          )}
        </div>
        <div className="relative">
          <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]" />
          <input
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            autoComplete="current-password"
            required
            type={showPassword ? "text" : "password"}
            disabled={isSendingOtp}
            className="ui-focus h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] pl-10 pr-10 text-sm text-[var(--text)] placeholder:text-[var(--text-subtle)] disabled:opacity-70"
            placeholder="Enter password"
          />
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-subtle)] hover:text-[var(--text)] focus:outline-none"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSendingOtp}
        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[var(--accent)] px-4 text-sm font-semibold text-[var(--text-inverse)] transition hover:bg-[var(--accent-hover)] disabled:opacity-70"
      >
        <LogIn className="h-4 w-4" />
        {isSendingOtp ? (kind === "user" ? "Signing in..." : "Sending OTP...") : formText.submitCredentials}
      </button>

      {kind === "user" && (
        <div className="text-center text-sm mt-4">
          <span className="text-[var(--text-muted)]">First time logging in? </span>
          <a
            href="/activate"
            className="font-medium text-[var(--accent)] hover:underline"
          >
            Create Password
          </a>
        </div>
      )}
    </form>
  );
}
